import { useCallback, useEffect, useState } from 'react';
import UITextToolbarSplitSelect from 'rich-text-lib/components/editor/UITextToolbarSplitSelect';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { MAKE_ORDERED_LIST, MAKE_UNORDERED_LIST } from 'rich-text-lib/constants/usageTracking';
import useDebouncedEditorStateContext from '../contexts/useDebouncedEditorStateContext';
import { buildLiftListItemTransaction, isList, wrapInList } from '../utils/listUtils';
import { Tracker } from '../../utils/usageTracker';
import { PM_FORMATTING } from '../constants/events';
import { jsx as _jsx } from "react/jsx-runtime";
const LIST_TYPES_TO_TRACKING_ACTIONS = {
  number: MAKE_ORDERED_LIST,
  bullet: MAKE_UNORDERED_LIST
};
export function getListsInSelection(state, listType) {
  const listItemNodeDetails = [];
  const otherNodeDetails = [];
  const {
    schema
  } = state;
  state.doc.nodesBetween(state.selection.from, state.selection.to, (node, pos, parent) => {
    if (isList(schema, node) && node.type !== listType) {
      otherNodeDetails.push({
        node,
        pos
      });
      return false;
    } else if (node.type === schema.nodes.list_item && parent && parent.type === listType) {
      listItemNodeDetails.push({
        node,
        pos
      });
    }
    return true;
  });
  return {
    listItemNodeDetails,
    otherNodeDetails
  };
}
export function toggleList(listType) {
  return (state, dispatch) => {
    const {
      otherNodeDetails,
      listItemNodeDetails
    } = getListsInSelection(state, listType);
    if (otherNodeDetails.length) {
      let tr = state.tr;
      otherNodeDetails.forEach(nodeDetails => {
        tr = tr.setNodeMarkup(nodeDetails.pos, listType);
      });
      if (dispatch) dispatch(tr);
      return true;
    }
    if (listItemNodeDetails.length) {
      let tr = state.tr;
      const schema = state.schema;
      listItemNodeDetails.forEach(nodeDetails => {
        const mappedPosition = tr.mapping.map(nodeDetails.pos);
        tr = buildLiftListItemTransaction(schema.nodes.list_item, tr, tr.doc.resolve(mappedPosition), tr.doc.resolve(mappedPosition + nodeDetails.node.nodeSize));
      });
      if (dispatch) dispatch(tr);
      return true;
    }
    return wrapInList(listType)(state, dispatch);
  };
}
export default function Lists() {
  const {
    editorState,
    setEditorState
  } = useDebouncedEditorStateContext();
  const [activeListType, setActiveListType] = useState('bullet');
  const [selectionHasList, setSelectionHasList] = useState(false);
  useEffect(() => {
    const {
      otherNodeDetails
    } = getListsInSelection(editorState, editorState.schema.nodes.bullet_list);
    if (otherNodeDetails.length) {
      setActiveListType('number');
    } else {
      setActiveListType('bullet');
    }
  }, [editorState, setActiveListType]);
  useEffect(() => {
    const {
      otherNodeDetails,
      listItemNodeDetails
    } = getListsInSelection(editorState, editorState.schema.nodes.bullet_list);
    if (otherNodeDetails.length || listItemNodeDetails.length) {
      setSelectionHasList(true);
    } else {
      setSelectionHasList(false);
    }
  }, [editorState]);
  const handleButtonClick = useCallback(listType => {
    const schema = editorState.schema;
    const lType = typeof listType === 'string' ? listType : activeListType;
    const nodeType = lType === 'bullet' ? schema.nodes.bullet_list : schema.nodes.ordered_list;
    toggleList(nodeType)(editorState, tr => {
      setEditorState(editorState.apply(tr));
    });
    Tracker.track(PM_FORMATTING, {
      action: LIST_TYPES_TO_TRACKING_ACTIONS[lType],
      method: 'toolbar'
    });
  }, [editorState, setEditorState, activeListType]);
  const changeListType = useCallback(evt => {
    setActiveListType(evt.target.value);
    handleButtonClick(evt.target.value);
  }, [setActiveListType, handleButtonClick]);
  return /*#__PURE__*/_jsx(UITextToolbarSplitSelect, {
    options: [{
      icon: 'bulletList',
      value: 'bullet',
      // @ts-expect-error not in types
      'data-test-id': 'bullet-list'
    }, {
      icon: 'numberList',
      value: 'number',
      'data-test-id': 'number-list'
    }],
    "data-test-id": "list-style-dropdown",
    onChange: changeListType,
    onButtonClick: handleButtonClick,
    active: selectionHasList,
    value: activeListType,
    tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "hubspot-prosemirror.tooltips.lists"
    })
  });
}